import React, { useState } from 'react';

interface EditableEmailProps {
  email: string;
  onUpdate: (newEmail: string) => Promise<void>;
  className?: string;
  clientId?: number;
}

const EditableEmail: React.FC<EditableEmailProps> = ({ email, onUpdate, className = '', clientId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedEmail, setEditedEmail] = useState(email);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEdit = () => {
    setIsEditing(true);
    setEditedEmail(email);
    setError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedEmail(email);
    setError(null);
  };

  const handleSave = async () => {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editedEmail)) {
      setError('Invalid email format');
      return;
    }

    if (editedEmail === email) {
      setIsEditing(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onUpdate(editedEmail);
      setIsEditing(false);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update email');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <input
          type="email"
          value={editedEmail}
          onChange={(e) => setEditedEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          className={`flex-1 min-w-0 px-3 py-1.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
            error ? 'border-red-500' : 'border-gray-300'
          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          autoFocus
          style={{ minWidth: '200px', maxWidth: '400px' }}
        />
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-3 py-1.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {loading ? 'Saving...' : '✓'}
        </button>
        <button
          onClick={handleCancel}
          disabled={loading}
          className="px-3 py-1.5 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          ✕
        </button>
        {error && (
          <span className="text-sm text-red-600 ml-2">{error}</span>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span 
        className="text-sm text-gray-900 truncate block min-w-0 flex-1" 
        title={email}
        style={{ maxWidth: '200px' }}
      >
        {email}
      </span>
      <button
        onClick={handleEdit}
        className="p-1 text-gray-500 hover:text-accent-600 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-1 rounded transition flex-shrink-0"
        title={`Edit email: ${email}`}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      </button>
    </div>
  );
};

export default EditableEmail;

